import type { ShipTime } from '@felix/domain';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionIcon,
  Alert,
  type AlertProps,
  Button,
  Modal,
  Textarea,
  Tooltip,
} from '@mantine/core';
import {
  IconArrowDown,
  IconArrowUp,
  IconCircleCheck,
  IconCloudUpload,
  IconEye,
  IconTrash,
} from '@tabler/icons-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import docs from '../documents/Documents.module.css';
import { FileKindIcon } from '../documents/parts.js';
import classes from './Forms.module.css';
import { type ReasonValues, reasonSchema } from './record-forms.js';
import { fileKindOf, signatureHeading, signatureRows, sortedFiles } from './record-model.js';
import { fileSizeText, formErrorText, type RecordBadge, recordTime, shortHash } from './status.js';
import type { DraftFile, FormRecordDetail, FormRecordFile, OfficeFileState } from './types.js';

/**
 * Uyarı kutusu: token tonları (warning-bg, warning-fg lacivertle koyulaştırılmış). Mantine'in sarı
 * tonu küçük yazıda WCAG AA kontrastını sağlamıyor (axe "color-contrast").
 */
export function WarningAlert(props: Omit<AlertProps, 'color' | 'classNames'>) {
  return (
    <Alert
      {...props}
      color="yellow"
      classNames={{
        root: classes.warnRoot,
        title: classes.warnText,
        message: classes.warnText,
        icon: classes.warnText,
      }}
    />
  );
}

/** Durum, Amended ve işaret rozetleri (ADR 0042 §4, §13). */
export function RecordBadges({ badges }: { badges: readonly RecordBadge[] }) {
  return (
    <span className={classes.badges}>
      {badges.map((badge) => (
        <StatusBadge key={badge.key} tone={badge.tone}>
          {badge.label}
        </StatusBadge>
      ))}
    </span>
  );
}

/** Teslim işareti (mimari belge §6.4): ✓ gemide kaydedildi, ✓✓ ofise ulaştı. */
export function DeliveryMark({ delivery }: { delivery: 'ship' | 'office' }) {
  const { t } = useTranslation();
  return (
    <span className={classes.delivery} data-delivery={delivery}>
      <span aria-hidden>{delivery === 'office' ? '✓✓' : '✓'}</span>
      {t(`forms.delivery.${delivery}`)}
    </span>
  );
}

const OFFICE_TONE: Record<OfficeFileState, StatusTone> = {
  verified: 'success',
  uploading: 'info',
  on_vessel: 'neutral',
  rejected: 'danger',
};

/**
 * Ofiste dosya durumu (ADR 0038 §14, #164; ADR 0042 Ek U30): Available, Uploading n%
 * (`_ship_files.bytes_received`), Waiting for upload window (gemi kuyruğu pencere bekliyor),
 * On vessel (aktarım başlamadı; USB ile senkronlanan gemide dosya gemide kalır), Rejected.
 */
export function OfficeFileBadge({
  state,
  progress = null,
  waitingWindow = false,
}: {
  state: OfficeFileState;
  /** Aktarılan oran (0–100); biliniyorsa. */
  progress?: number | null;
  /** Gemi kuyruğu yükleme penceresini bekliyor (`vessel_file_status.upload_state`). */
  waitingWindow?: boolean;
}) {
  const { t } = useTranslation();
  let label = t(`forms.files.office.${state}`);
  if (state === 'uploading' && progress !== null) {
    label = t('forms.files.office.uploadingPercent', { percent: Math.floor(progress) });
  } else if (state === 'on_vessel' && waitingWindow) {
    label = t('forms.files.office.window');
  }
  return (
    <StatusBadge tone={OFFICE_TONE[state]} variant="dot">
      {label}
    </StatusBadge>
  );
}

type ListFile = DraftFile | FormRecordFile;

export interface RecordFileListProps {
  files: readonly ListFile[];
  /** Taslakta sıralama ve çıkarma. */
  editable?: boolean;
  /** Önizlenen dosyanın SHA-256'sı. */
  selected?: string | null;
  onSelect?: (file: ListFile) => void;
  onMove?: (index: number, step: -1 | 1) => void;
  onRemove?: (index: number) => void;
  /** Satırın sonundaki durum (ofiste dosya durumu, gemide yükleme). */
  status?: ((file: ListFile) => ReactNode) | undefined;
  empty?: string;
}

/**
 * Kaydın dosyaları (ADR 0042 §6): sıra, tür, ad, kaynak (Office, tarama, fotoğraf, doldurulan
 * PDF), boyut ve SHA-256'nın ilk 12 hanesi. Sayfalar sırayla önizlenir; taslakta sıralanır ve
 * çıkarılır (çıkarılan dosya depoda kalır).
 */
export function RecordFileList({
  files,
  editable = false,
  selected = null,
  onSelect,
  onMove,
  onRemove,
  status,
  empty,
}: RecordFileListProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const list = sortedFiles(files);
  if (list.length === 0) {
    return <p className={classes.muted}>{empty ?? t('forms.files.none')}</p>;
  }
  return (
    <ol className={classes.fileList} aria-label={t('forms.files.title')}>
      {list.map((file, index) => {
        const kind = fileKindOf(file);
        const active = selected === file.sha256;
        return (
          <li
            key={`${file.sha256}-${file.sortOrder}`}
            className={classes.fileRow}
            data-active={active || undefined}
          >
            <span className={classes.fileOrder} aria-hidden>
              {index + 1}
            </span>
            <FileKindIcon kind={kind} variant="glyph" size={20} />
            <span className={classes.fileText}>
              {onSelect ? (
                <button
                  type="button"
                  className={classes.fileName}
                  title={file.name}
                  aria-current={active || undefined}
                  onClick={() => onSelect(file)}
                >
                  {file.name}
                </button>
              ) : (
                <span className={classes.fileName} title={file.name}>
                  {file.name}
                </span>
              )}
              <span className={classes.fileMeta}>
                {t(`forms.fileSources.${file.source}`)} · {fileSizeText(file.size, locale)} ·{' '}
                <span title={file.sha256}>{shortHash(file.sha256)}</span>
              </span>
            </span>
            {status ? <span className={classes.fileStatus}>{status(file)}</span> : null}
            <span className={classes.fileTools}>
              {onSelect ? (
                <Tooltip label={t('forms.files.preview')} withArrow>
                  <ActionIcon
                    variant="subtle"
                    aria-label={t('forms.files.previewName', { name: file.name })}
                    onClick={() => onSelect(file)}
                  >
                    <IconEye size={16} />
                  </ActionIcon>
                </Tooltip>
              ) : null}
              {editable && onMove ? (
                <>
                  <ActionIcon
                    variant="subtle"
                    disabled={index === 0}
                    aria-label={t('forms.files.up', { name: file.name })}
                    onClick={() => onMove(index, -1)}
                  >
                    <IconArrowUp size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    disabled={index === list.length - 1}
                    aria-label={t('forms.files.down', { name: file.name })}
                    onClick={() => onMove(index, 1)}
                  >
                    <IconArrowDown size={16} />
                  </ActionIcon>
                </>
              ) : null}
              {editable && onRemove ? (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  aria-label={t('forms.files.remove', { name: file.name })}
                  onClick={() => onRemove(index)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              ) : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Gemide gönderilmiş dosyanın buluta çıkışı (ortak kuyruk; ADR 0042 §7): bekliyor (pencere ve
 * öncelik kuyruğun politikasıdır), yükleniyor, buluta ulaştı, başarısız.
 */
export function ShipTransferMark({ transfer }: { transfer: string | null }) {
  const { t } = useTranslation();
  if (transfer === null) return null;
  const done = transfer === 'verified';
  const state = ['pending', 'uploading', 'verified', 'failed'].includes(transfer)
    ? transfer
    : 'pending';
  return (
    <span className={classes.transfer} data-state={state}>
      {done ? <IconCircleCheck size={14} aria-hidden /> : <IconCloudUpload size={14} aria-hidden />}
      {t(`forms.files.ship.${state}`)}
    </span>
  );
}

/** Bilgi kartının satırı (etiket üstte, değer altta; gemi-ism-formlari.jpeg "Form Information"). */
export function Fact({
  label,
  children,
  title,
}: {
  label: string;
  children: ReactNode;
  title?: string | undefined;
}) {
  return (
    <div className={docs.sideFact}>
      <dt>{label}</dt>
      <dd title={title}>{children}</dd>
    </div>
  );
}

/**
 * Gerekçe penceresi, parolasız (taslaktan vazgeçme; ADR 0042 §12, §14). Parola isteyen işlemler
 * PMS'in parola penceresini kullanır (ikinci bir parola penceresi yazılmaz).
 */
export function ReasonDialog({
  opened,
  title,
  body,
  confirmLabel,
  danger = false,
  onClose,
  onConfirm,
}: {
  opened: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<unknown>(null);
  const form = useForm<ReasonValues>({
    resolver: zodResolver(reasonSchema),
    defaultValues: { reason: '' },
  });
  useEffect(() => {
    if (opened) {
      form.reset({ reason: '' });
      setError(null);
    }
  }, [opened, form]);
  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await onConfirm(values.reason);
    } catch (caught) {
      setError(caught);
    }
  });
  const fieldError = form.formState.errors.reason?.message;
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      closeButtonProps={{ 'aria-label': t('forms.dialog.back') }}
    >
      <form onSubmit={submit} noValidate>
        <div className={classes.dialogBody}>{body}</div>
        <Textarea
          label={t('forms.dialog.reason')}
          required
          autosize
          minRows={2}
          maxLength={2000}
          {...form.register('reason')}
          error={fieldError ? t(`forms.errors.${fieldError}`) : undefined}
        />
        {error ? (
          <p className={classes.error} role="alert">
            {formErrorText(t, error)}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('forms.dialog.back')}
          </Button>
          <Button
            type="submit"
            {...(danger ? { color: 'red' } : {})}
            loading={form.formState.isSubmitting}
          >
            {confirmLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Onay adım göstergesi (ortağın geri bildirimi 2026-10-17; ADR 0069): zincirin adımları sırayla;
 * her adımda rol, kişi, karar ve zaman. Sıradaki adım vurgulu (`aria-current="step"`); sonrakiler
 * "Not yet". Zincir yoksa (eski sunucu, taslak) çizilmez.
 */
export function ApprovalSteps({
  record,
  timeZone,
}: {
  record: Pick<FormRecordDetail, 'chain' | 'pending' | 'prepared' | 'names' | 'roles'>;
  timeZone: ShipTime | null;
}) {
  const { t } = useTranslation();
  const rows = signatureRows(record);
  if (rows.length === 0) return null;
  return (
    <ol className={classes.stepList} aria-label={t('forms.steps.title')}>
      {rows.map((row, index) => {
        const decision = row.decision;
        const state = decision ? decision.decision : row.current ? 'current' : 'upcoming';
        return (
          <li
            key={row.key}
            className={classes.stepItem}
            data-state={state}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            <span className={classes.stepNo} aria-hidden>
              {index + 1}
            </span>
            <span className={classes.stepBody}>
              <span className={classes.stepRole}>
                <span className={classes.srOnly}>
                  {t('forms.steps.step', { step: index + 1 })}{' '}
                </span>
                {signatureHeading(row, t, (id) => record.roles?.[id])}
                {row.office ? ` · ${t('forms.revisions.officeStep')}` : ''}
              </span>
              <span className={classes.stepText}>
                {decision
                  ? `${record.names[decision.approverId] ?? '—'} · ${t(
                      row.prepared ? 'forms.steps.submitted' : `forms.status.${decision.decision}`,
                    )} · ${recordTime(decision.at, timeZone)}`
                  : t(state === 'current' ? 'forms.steps.awaiting' : 'forms.steps.notYet')}
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
